import { useState, useEffect, useMemo, useRef } from 'react';
import { motion } from 'framer-motion';
import { Plus, BedDouble, Receipt, DollarSign, AlertCircle, FileText, Edit, Eye, Search, Filter, Calendar, X, RefreshCw, Download, FileSpreadsheet, Package, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { PatientSearchDropdown, FixedChargeSearchDropdown } from '@/components/SearchDropdowns';
import api from '@/lib/api';
import { toast } from 'sonner';
import { downloadCSV, downloadExcel, EXPORT_COLUMNS } from '@/lib/exportUtils';

export default function Admissions() {
  const [admissions, setAdmissions] = useState([]);
  const [patients, setPatients] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [fixedCharges, setFixedCharges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [dischargeDialogOpen, setDischargeDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedAdmission, setSelectedAdmission] = useState(null);
  const [generateBill, setGenerateBill] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  
  // Advanced filter states
  const [doctorFilter, setDoctorFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  
  const [billingItems, setBillingItems] = useState([
    { description: 'Room Charges', amount: 0, quantity: 1, tax_percentage: 0, is_fixed_charge: false },
    { description: 'Medical Services', amount: 0, quantity: 1, tax_percentage: 0, is_fixed_charge: false }
  ]);
  
  const [dischargeSummary, setDischargeSummary] = useState({
    diagnosis_at_discharge: '',
    treatment_given: '',
    medications_prescribed: '',
    follow_up_instructions: '',
    condition_at_discharge: 'stable',
    diet_instructions: '',
    activity_restrictions: '',
    next_visit_date: '',
    doctor_notes: ''
  });
  const user = JSON.parse(localStorage.getItem('user'));
  const [formData, setFormData] = useState({
    patient_id: '',
    doctor_id: user.id,
    room_number: '',
    admission_reason: '',
    notes: ''
  });
  const [editFormData, setEditFormData] = useState({
    room_number: '',
    admission_reason: '',
    notes: '',
    doctor_id: ''
  });

  // Advanced filtering for admissions
  const filteredAdmissions = useMemo(() => {
    let filtered = admissions;
    
    // Text search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(admission => {
        const patient = patients.find(p => p.id === admission.patient_id);
        const doctor = doctors.find(d => d.id === admission.doctor_id);
        return (
          admission.room_number?.toLowerCase().includes(query) ||
          admission.admission_reason?.toLowerCase().includes(query) ||
          patient?.full_name?.toLowerCase().includes(query) ||
          patient?.patient_number?.toLowerCase().includes(query) ||
          doctor?.full_name?.toLowerCase().includes(query)
        );
      });
    }
    
    // Doctor filter
    if (doctorFilter && doctorFilter !== 'all') {
      filtered = filtered.filter(admission => admission.doctor_id === doctorFilter);
    }
    
    // Status filter
    if (statusFilter && statusFilter !== 'all') {
      filtered = filtered.filter(admission => admission.status === statusFilter);
    }
    
    // Date range filter
    if (dateFrom) {
      filtered = filtered.filter(admission => {
        const admissionDate = new Date(admission.admission_date).toISOString().split('T')[0];
        return admissionDate >= dateFrom;
      });
    }
    if (dateTo) {
      filtered = filtered.filter(admission => {
        const admissionDate = new Date(admission.admission_date).toISOString().split('T')[0];
        return admissionDate <= dateTo;
      });
    }
    
    return filtered;
  }, [admissions, patients, doctors, searchQuery, doctorFilter, statusFilter, dateFrom, dateTo]);

  // Clear all filters
  const clearFilters = () => {
    setSearchQuery('');
    setDoctorFilter('all');
    setStatusFilter('all');
    setDateFrom('');
    setDateTo('');
  };

  const hasActiveFilters = searchQuery || doctorFilter !== 'all' || statusFilter !== 'all' || dateFrom || dateTo;

  // Export handlers
  const handleExportCSV = () => {
    if (filteredAdmissions.length === 0) {
      toast.error('No data to export');
      return;
    }
    // Enrich data with patient and doctor names
    const enrichedData = filteredAdmissions.map(admission => {
      const patient = patients.find(p => p.id === admission.patient_id);
      const doctor = doctors.find(d => d.id === admission.doctor_id);
      return {
        ...admission,
        patient_name: patient?.full_name || 'N/A',
        patient_number: patient?.patient_number || 'N/A',
        doctor_name: doctor?.full_name || 'N/A'
      };
    });
    downloadCSV(enrichedData, EXPORT_COLUMNS.admissions, 'admissions_export');
    toast.success(`Exported ${filteredAdmissions.length} admission(s) to CSV`);
  };

  const handleExportExcel = () => {
    if (filteredAdmissions.length === 0) {
      toast.error('No data to export');
      return;
    }
    const enrichedData = filteredAdmissions.map(admission => {
      const patient = patients.find(p => p.id === admission.patient_id);
      const doctor = doctors.find(d => d.id === admission.doctor_id);
      return {
        ...admission,
        patient_name: patient?.full_name || 'N/A',
        patient_number: patient?.patient_number || 'N/A',
        doctor_name: doctor?.full_name || 'N/A'
      };
    });
    downloadExcel(enrichedData, EXPORT_COLUMNS.admissions, 'admissions_export');
    toast.success(`Exported ${filteredAdmissions.length} admission(s) to Excel`);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [admissionsRes, patientsRes, doctorsRes, fixedChargesRes] = await Promise.all([
        api.get('/admissions'),
        api.get('/patients'),
        api.get('/users/doctors'),
        api.get('/fixed-charges')
      ]);
      setAdmissions(admissionsRes.data);
      setPatients(patientsRes.data);
      setDoctors(doctorsRes.data);
      setFixedCharges(fixedChargesRes.data?.filter(c => c.is_active) || []);
    } catch (error) {
      toast.error('Failed to fetch data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await api.post('/admissions', formData);
      toast.success('Patient admitted successfully!');
      setDialogOpen(false);
      setFormData({
        patient_id: '',
        doctor_id: user.id,
        room_number: '',
        admission_reason: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to admit patient');
    }
  };

  const handleViewAdmission = (admission) => {
    setSelectedAdmission(admission);
    setViewDialogOpen(true);
  };

  const handleEditAdmission = (admission) => {
    setSelectedAdmission(admission);
    setEditFormData({
      room_number: admission.room_number || '',
      admission_reason: admission.admission_reason || '',
      notes: admission.notes || '',
      doctor_id: admission.doctor_id || ''
    });
    setEditDialogOpen(true);
  };

  const handleUpdateAdmission = async (e) => {
    e.preventDefault();
    if (!selectedAdmission) return;
    
    try {
      await api.put(`/admissions/${selectedAdmission.id}`, editFormData);
      toast.success('Admission updated successfully!');
      setEditDialogOpen(false);
      setSelectedAdmission(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update admission');
    }
  };

  const handleDischarge = async (admissionId) => {
    try {
      await api.put(`/admissions/${admissionId}/discharge`);
      toast.success('Patient discharged successfully!');
      fetchData();
    } catch (error) {
      toast.error('Failed to discharge patient');
    }
  };

  const handleDischargeWithBilling = async () => {
    if (!selectedAdmission) return;
    
    // Validate discharge summary
    if (!dischargeSummary.diagnosis_at_discharge || !dischargeSummary.treatment_given || 
        !dischargeSummary.medications_prescribed || !dischargeSummary.follow_up_instructions) {
      toast.error('Please fill in all required discharge summary fields');
      return;
    }
    
    try {
      // First create discharge summary
      await api.post('/discharge-summary', {
        admission_id: selectedAdmission.id,
        patient_id: selectedAdmission.patient_id,
        doctor_id: user.id,
        ...dischargeSummary
      });
      
      // Then discharge the patient
      await api.put(`/admissions/${selectedAdmission.id}/discharge`);
      
      // Then create billing if requested
      if (generateBill) {
        const total = billingItems.reduce((sum, item) => sum + (parseFloat(item.amount) || 0), 0);
        if (total > 0) {
          await api.post('/billing', {
            patient_id: selectedAdmission.patient_id,
            items: billingItems.filter(item => item.amount > 0),
            notes: `Discharge billing for admission in Room ${selectedAdmission.room_number}`,
            admission_id: selectedAdmission.id
          });
          toast.success('Patient discharged with summary and billing created successfully!');
        } else {
          toast.success('Patient discharged with summary created successfully!');
        }
      } else {
        toast.success('Patient discharged with summary created successfully!');
      }
      
      setDischargeDialogOpen(false);
      setSelectedAdmission(null);
      setBillingItems([
        { description: 'Room Charges', amount: 0 },
        { description: 'Medical Services', amount: 0 }
      ]);
      setDischargeSummary({
        diagnosis_at_discharge: '',
        treatment_given: '',
        medications_prescribed: '',
        follow_up_instructions: '',
        condition_at_discharge: 'stable',
        diet_instructions: '',
        activity_restrictions: '',
        next_visit_date: '',
        doctor_notes: ''
      });
      setGenerateBill(true);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to discharge patient');
    }
  };

  const handleAddBillingItem = () => {
    setBillingItems([...billingItems, { description: '', amount: 0, quantity: 1, tax_percentage: 0, is_fixed_charge: false }]);
  };

  const handleBillingItemChange = (index, field, value) => {
    const newItems = [...billingItems];
    if (field === 'amount') {
      newItems[index][field] = parseFloat(value) || 0;
    } else if (field === 'quantity') {
      newItems[index][field] = parseInt(value) || 1;
    } else if (field === 'tax_percentage') {
      newItems[index][field] = parseFloat(value) || 0;
    } else {
      newItems[index][field] = value;
    }
    setBillingItems(newItems);
  };

  const handleRemoveBillingItem = (index) => {
    if (billingItems.length <= 1) {
      toast.error('At least one billing item is required');
      return;
    }
    setBillingItems(billingItems.filter((_, i) => i !== index));
  };

  // Handle adding a fixed charge
  const handleAddFixedCharge = (charge) => {
    setBillingItems([...billingItems, {
      description: charge.charge_name,
      amount: charge.rate || charge.default_rate,
      quantity: 1,
      tax_percentage: charge.tax_percentage,
      is_fixed_charge: true,
      fixed_charge_id: charge.id,
      category: charge.category
    }]);
    toast.success(`Added: ${charge.charge_name}`);
  };

  // Calculate billing totals with tax
  const billingTotals = useMemo(() => {
    let subtotal = 0;
    let totalTax = 0;
    
    billingItems.forEach(item => {
      const itemTotal = (item.amount || 0) * (item.quantity || 1);
      const itemTax = itemTotal * ((item.tax_percentage || 0) / 100);
      subtotal += itemTotal;
      totalTax += itemTax;
    });
    
    return {
      subtotal: subtotal.toFixed(2),
      tax: totalTax.toFixed(2),
      total: (subtotal + totalTax).toFixed(2)
    };
  }, [billingItems]);

  const calculateTotal = () => {
    return parseFloat(billingTotals.total) || 0;
  };

  const calculateDaysAdmitted = (admissionDate) => {
    const admitted = new Date(admissionDate);
    const now = new Date();
    const diffTime = Math.abs(now - admitted);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#4A238F]"></div>
      </div>
    );
  }

  return (
    <div data-testid="admissions-page">
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.3 }}
      >
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Inter, sans-serif' }}>
              Admissions
            </h1>
            <p className="text-slate-600 mt-2">Manage patient admissions and discharges</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="admit-patient-button" className="bg-[#4A238F] hover:bg-[#3A1B72] text-white">
                <Plus className="mr-2 h-4 w-4" />
                Admit Patient
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Admit New Patient</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="patient_id">Patient *</Label>
                  <PatientSearchDropdown
                    patients={patients}
                    selectedPatientId={formData.patient_id}
                    onSelect={(patientId) => setFormData({...formData, patient_id: patientId})}
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="room_number">Room Number *</Label>
                  <Input
                    id="room_number"
                    value={formData.room_number}
                    onChange={(e) => setFormData({...formData, room_number: e.target.value})}
                    data-testid="admission-room-input"
                    placeholder="e.g., 101"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="admission_reason">Admission Reason *</Label>
                  <Input
                    id="admission_reason"
                    value={formData.admission_reason}
                    onChange={(e) => setFormData({...formData, admission_reason: e.target.value})}
                    data-testid="admission-reason-input"
                    placeholder="Reason for admission"
                    required
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="notes">Notes</Label>
                  <textarea
                    id="notes"
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-slate-50"
                    placeholder="Additional notes..."
                  />
                </div>

                <Button type="submit" data-testid="submit-admission-button" className="w-full bg-[#4A238F] hover:bg-[#3A1B72] text-white">
                  Admit Patient
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Search and Filters */}
        <Card className="mb-6 border border-slate-200 bg-white">
          <CardContent className="p-4">
            {/* Search Box */}
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by Room #, Patient name, Doctor, or Reason..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 pr-10"
                data-testid="admission-search-input"
              />
              {searchQuery && (
                <button 
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>

            {/* Filter Row */}
            <div className="flex flex-wrap gap-3 items-center">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-slate-500" />
                <span className="text-sm text-slate-600 font-medium">Filters:</span>
              </div>

              {/* Doctor Filter */}
              <Select value={doctorFilter} onValueChange={setDoctorFilter}>
                <SelectTrigger className="w-[180px]" data-testid="admission-doctor-filter">
                  <SelectValue placeholder="Attending Doctor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Doctors</SelectItem>
                  {doctors.map((doctor) => (
                    <SelectItem key={doctor.id} value={doctor.id}>
                      Dr. {doctor.full_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-[140px]" data-testid="admission-status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="admitted">Admitted</SelectItem>
                  <SelectItem value="discharged">Discharged</SelectItem>
                </SelectContent>
              </Select>

              {/* Date From */}
              <div className="flex items-center gap-1">
                <Calendar className="h-4 w-4 text-slate-400" />
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e.target.value)}
                  className="w-[140px]"
                  data-testid="admission-date-from"
                />
              </div>

              {/* Date To */}
              <div className="flex items-center gap-1">
                <span className="text-slate-400">to</span>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e.target.value)}
                  className="w-[140px]"
                  data-testid="admission-date-to"
                />
              </div>

              {/* Refresh Button */}
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="h-4 w-4" />
              </Button>

              {/* Clear Filters */}
              {hasActiveFilters && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  onClick={clearFilters}
                  className="text-slate-500 hover:text-slate-700"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear filters
                </Button>
              )}

              {/* Export Buttons */}
              <div className="ml-auto flex gap-2">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm">
                      <Download className="h-4 w-4 mr-2" />
                      Export
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent>
                    <DropdownMenuItem onClick={handleExportCSV}>
                      <FileText className="mr-2 h-4 w-4" />
                      Export as CSV
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleExportExcel}>
                      <FileSpreadsheet className="mr-2 h-4 w-4" />
                      Export as Excel
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>

            {/* Results count */}
            <div className="mt-3 text-sm text-slate-500">
              Showing {filteredAdmissions.length} admission(s)
              {hasActiveFilters && ' (filtered)'}
            </div>
          </CardContent>
        </Card>

        {/* Admissions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredAdmissions.length === 0 ? (
            <div className="col-span-full">
              <Card className="border border-slate-200">
                <CardContent className="p-12 text-center">
                  <p className="text-slate-500">
                    {searchQuery ? `No admissions found matching "${searchQuery}"` : 'No admissions found'}
                  </p>
                </CardContent>
              </Card>
            </div>
          ) : (
            filteredAdmissions.map((admission) => {
              const patient = patients.find(p => p.id === admission.patient_id);
              const doctor = doctors.find(d => d.id === admission.doctor_id);
              
              return (
                <motion.div
                  key={admission.id}
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.2 }}
                >
                  <Card 
                    className="border border-slate-200 shadow-sm hover:shadow-md transition-all cursor-pointer"
                    onClick={() => handleViewAdmission(admission)}
                    data-testid={`admission-card-${admission.id}`}
                  >
                    <CardContent className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className="h-10 w-10 rounded-full bg-purple-100 flex items-center justify-center">
                            <BedDouble className="h-5 w-5 text-purple-600" />
                          </div>
                          <div>
                            <p className="font-semibold text-lg text-slate-900">Room {admission.room_number}</p>
                            <span className={`inline-block text-xs px-2 py-1 rounded mt-1 ${
                              admission.status === 'admitted' ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-100 text-slate-700'
                            }`}>
                              {admission.status}
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="space-y-2">
                        <div>
                          <p className="text-sm text-slate-600">Patient</p>
                          <p className="font-medium text-slate-900">{patient?.full_name}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-600">Doctor</p>
                          <p className="font-medium text-slate-900">Dr. {doctor?.full_name}</p>
                        </div>
                        <div>
                          <p className="text-sm text-slate-600">Reason</p>
                          <p className="text-slate-900">{admission.admission_reason}</p>
                        </div>
                        {admission.notes && (
                          <div>
                            <p className="text-sm text-slate-600">Notes</p>
                            <p className="text-sm text-slate-700">{admission.notes}</p>
                          </div>
                        )}
                        <div>
                          <p className="text-sm text-slate-600">Admitted</p>
                          <p className="text-sm text-slate-700">{new Date(admission.admission_date).toLocaleDateString()}</p>
                        </div>
                      </div>
                      {admission.status === 'admitted' && (
                        <div className="flex gap-2 mt-4" onClick={(e) => e.stopPropagation()}>
                          <Button
                            onClick={() => handleViewAdmission(admission)}
                            data-testid={`view-admission-${admission.id}`}
                            variant="outline"
                            size="sm"
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            onClick={() => handleEditAdmission(admission)}
                            data-testid={`edit-admission-${admission.id}`}
                            variant="outline"
                            className="flex-1"
                          >
                            <Edit className="mr-2 h-4 w-4" />
                            Edit
                          </Button>
                          <Button
                            onClick={() => {
                              setSelectedAdmission(admission);
                              const daysAdmitted = calculateDaysAdmitted(admission.admission_date);
                              setBillingItems([
                                { description: 'Room Charges', amount: daysAdmitted * 100 },
                                { description: 'Medical Services', amount: 200 },
                                { description: 'Nursing Care', amount: 150 }
                              ]);
                              setDischargeDialogOpen(true);
                            }}
                            data-testid={`discharge-patient-${admission.id}`}
                            className="flex-1 bg-[#4A238F] hover:bg-[#3A1B72] text-white"
                          >
                            <Receipt className="mr-2 h-4 w-4" />
                            Discharge
                          </Button>
                        </div>
                      )}
                      {admission.status === 'discharged' && (
                        <div className="flex gap-2 mt-4" onClick={(e) => e.stopPropagation()}>
                          <Button
                            onClick={() => handleViewAdmission(admission)}
                            data-testid={`view-admission-${admission.id}`}
                            variant="outline"
                            className="w-full"
                          >
                            <Eye className="mr-2 h-4 w-4" />
                            View Details
                          </Button>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })
          )}
        </div>

        {/* View Admission Details Dialog */}
        <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <BedDouble className="h-5 w-5 text-[#4A238F]" />
                Admission Details
              </DialogTitle>
            </DialogHeader>
            {selectedAdmission && (
              <div className="space-y-4">
                {/* Status Badge */}
                <div className="flex justify-between items-center">
                  <Badge className={`${
                    selectedAdmission.status === 'admitted' 
                      ? 'bg-emerald-100 text-emerald-700' 
                      : 'bg-slate-100 text-slate-700'
                  }`}>
                    {selectedAdmission.status?.toUpperCase()}
                  </Badge>
                  <span className="text-sm text-slate-500">
                    {calculateDaysAdmitted(selectedAdmission.admission_date)} days
                  </span>
                </div>

                {/* Patient & Room Info */}
                <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                  <div>
                    <p className="text-sm text-slate-600">Patient</p>
                    <p className="font-semibold text-slate-900">
                      {patients.find(p => p.id === selectedAdmission.patient_id)?.full_name || 'Unknown'}
                    </p>
                    <p className="text-xs text-slate-500">
                      {patients.find(p => p.id === selectedAdmission.patient_id)?.patient_number}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Room Number</p>
                    <p className="font-semibold text-slate-900">{selectedAdmission.room_number}</p>
                  </div>
                </div>

                {/* Doctor & Dates */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-slate-600">Attending Doctor</p>
                    <p className="font-medium text-slate-900">
                      Dr. {doctors.find(d => d.id === selectedAdmission.doctor_id)?.full_name || 'Unknown'}
                    </p>
                  </div>
                  <div>
                    <p className="text-sm text-slate-600">Admission Date</p>
                    <p className="font-medium text-slate-900">
                      {new Date(selectedAdmission.admission_date).toLocaleDateString()}
                    </p>
                  </div>
                </div>

                {/* Discharge Date if discharged */}
                {selectedAdmission.status === 'discharged' && selectedAdmission.discharge_date && (
                  <div>
                    <p className="text-sm text-slate-600">Discharge Date</p>
                    <p className="font-medium text-slate-900">
                      {new Date(selectedAdmission.discharge_date).toLocaleDateString()}
                    </p>
                  </div>
                )}

                {/* Admission Reason */}
                <div>
                  <p className="text-sm text-slate-600 mb-1">Admission Reason</p>
                  <p className="p-3 bg-slate-50 rounded-md text-slate-900">
                    {selectedAdmission.admission_reason}
                  </p>
                </div>

                {/* Notes */}
                {selectedAdmission.notes && (
                  <div>
                    <p className="text-sm text-slate-600 mb-1">Notes</p>
                    <p className="p-3 bg-slate-50 rounded-md text-slate-700">
                      {selectedAdmission.notes}
                    </p>
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-2 pt-4 border-t">
                  <Button variant="outline" onClick={() => setViewDialogOpen(false)} className="flex-1">
                    Close
                  </Button>
                  {selectedAdmission.status === 'admitted' && (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setViewDialogOpen(false);
                          handleEditAdmission(selectedAdmission);
                        }}
                        className="flex-1"
                      >
                        <Edit className="mr-2 h-4 w-4" />
                        Edit
                      </Button>
                      <Button
                        onClick={() => {
                          setViewDialogOpen(false);
                          const daysAdmitted = calculateDaysAdmitted(selectedAdmission.admission_date);
                          setBillingItems([
                            { description: 'Room Charges', amount: daysAdmitted * 100 },
                            { description: 'Medical Services', amount: 200 },
                            { description: 'Nursing Care', amount: 150 }
                          ]);
                          setDischargeDialogOpen(true);
                        }}
                        className="flex-1 bg-[#4A238F] hover:bg-[#3A1B72] text-white"
                      >
                        <Receipt className="mr-2 h-4 w-4" />
                        Discharge
                      </Button>
                    </>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Edit Admission Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>Edit Admission</DialogTitle>
            </DialogHeader>
            {selectedAdmission && (
              <form onSubmit={handleUpdateAdmission} className="space-y-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="text-sm text-slate-600">Patient</p>
                  <p className="font-semibold">{patients.find(p => p.id === selectedAdmission.patient_id)?.full_name}</p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="edit_room_number">Room Number *</Label>
                  <Input
                    id="edit_room_number"
                    value={editFormData.room_number}
                    onChange={(e) => setEditFormData({...editFormData, room_number: e.target.value})}
                    placeholder="e.g., 101"
                    required
                    data-testid="edit-room-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_admission_reason">Admission Reason *</Label>
                  <Input
                    id="edit_admission_reason"
                    value={editFormData.admission_reason}
                    onChange={(e) => setEditFormData({...editFormData, admission_reason: e.target.value})}
                    placeholder="Reason for admission"
                    required
                    data-testid="edit-reason-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_doctor">Attending Doctor</Label>
                  <Select value={editFormData.doctor_id} onValueChange={(value) => setEditFormData({...editFormData, doctor_id: value})}>
                    <SelectTrigger data-testid="edit-doctor-select">
                      <SelectValue placeholder="Select doctor" />
                    </SelectTrigger>
                    <SelectContent>
                      {doctors.map((doctor) => (
                        <SelectItem key={doctor.id} value={doctor.id}>
                          Dr. {doctor.full_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="edit_notes">Notes</Label>
                  <textarea
                    id="edit_notes"
                    value={editFormData.notes}
                    onChange={(e) => setEditFormData({...editFormData, notes: e.target.value})}
                    className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-white"
                    placeholder="Additional notes..."
                  />
                </div>

                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" data-testid="save-admission-edit" className="bg-[#4A238F] hover:bg-[#3A1B72] text-white">
                    Save Changes
                  </Button>
                </DialogFooter>
              </form>
            )}
          </DialogContent>
        </Dialog>

        {/* Discharge Dialog with Summary & Billing Integration */}
        <Dialog open={dischargeDialogOpen} onOpenChange={setDischargeDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Discharge Patient - Medical Summary & Billing</DialogTitle>
            </DialogHeader>
            {selectedAdmission && (
              <div className="space-y-4">
                {/* Patient Info */}
                <Card className="border border-slate-200 bg-slate-50">
                  <CardContent className="p-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div>
                        <p className="text-sm text-slate-600">Patient</p>
                        <p className="font-medium text-slate-900">
                          {patients.find(p => p.id === selectedAdmission.patient_id)?.full_name}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Room Number</p>
                        <p className="font-medium text-slate-900">{selectedAdmission.room_number}</p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Admission Date</p>
                        <p className="font-medium text-slate-900">
                          {new Date(selectedAdmission.admission_date).toLocaleDateString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-sm text-slate-600">Days Admitted</p>
                        <p className="font-medium text-slate-900">
                          {calculateDaysAdmitted(selectedAdmission.admission_date)} days
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Tabs for Discharge Summary and Billing */}
                <Tabs defaultValue="summary" className="space-y-4">
                  <TabsList className="grid w-full grid-cols-2 bg-slate-100 p-1.5 rounded-xl border border-slate-200 h-auto">
                    <TabsTrigger 
                      value="summary" 
                      data-testid="discharge-summary-tab"
                      className="data-[state=active]:bg-[#4A238F] data-[state=active]:text-white data-[state=inactive]:bg-transparent data-[state=inactive]:text-slate-700 data-[state=inactive]:hover:bg-slate-200 px-4 py-2 rounded-lg font-medium transition-all"
                    >
                      <FileText className="h-4 w-4 mr-2" />
                      Discharge Summary
                    </TabsTrigger>
                    <TabsTrigger 
                      value="billing" 
                      data-testid="billing-tab"
                      className="data-[state=active]:bg-[#4A238F] data-[state=active]:text-white data-[state=inactive]:bg-transparent data-[state=inactive]:text-slate-700 data-[state=inactive]:hover:bg-slate-200 px-4 py-2 rounded-lg font-medium transition-all"
                    >
                      <Receipt className="h-4 w-4 mr-2" />
                      Billing
                    </TabsTrigger>
                  </TabsList>

                  {/* Discharge Summary Tab */}
                  <TabsContent value="summary" className="space-y-4">
                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label htmlFor="diagnosis">Diagnosis at Discharge *</Label>
                        <Input
                          id="diagnosis"
                          value={dischargeSummary.diagnosis_at_discharge}
                          onChange={(e) => setDischargeSummary({...dischargeSummary, diagnosis_at_discharge: e.target.value})}
                          data-testid="diagnosis-input"
                          placeholder="Final diagnosis"
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="treatment">Treatment Given *</Label>
                        <textarea
                          id="treatment"
                          value={dischargeSummary.treatment_given}
                          onChange={(e) => setDischargeSummary({...dischargeSummary, treatment_given: e.target.value})}
                          className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-white"
                          placeholder="Describe treatment provided during admission..."
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="medications">Medications Prescribed *</Label>
                        <textarea
                          id="medications"
                          value={dischargeSummary.medications_prescribed}
                          onChange={(e) => setDischargeSummary({...dischargeSummary, medications_prescribed: e.target.value})}
                          className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-white"
                          placeholder="List medications with dosage and frequency..."
                          required
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="followup">Follow-up Instructions *</Label>
                        <textarea
                          id="followup"
                          value={dischargeSummary.follow_up_instructions}
                          onChange={(e) => setDischargeSummary({...dischargeSummary, follow_up_instructions: e.target.value})}
                          className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-md bg-white"
                          placeholder="Follow-up care instructions..."
                          required
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="condition">Condition at Discharge *</Label>
                          <Select
                            value={dischargeSummary.condition_at_discharge}
                            onValueChange={(value) => setDischargeSummary({...dischargeSummary, condition_at_discharge: value})}
                          >
                            <SelectTrigger data-testid="condition-select">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="improved">Improved</SelectItem>
                              <SelectItem value="stable">Stable</SelectItem>
                              <SelectItem value="critical">Critical</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="next_visit">Next Visit Date</Label>
                          <Input
                            id="next_visit"
                            type="date"
                            value={dischargeSummary.next_visit_date}
                            onChange={(e) => setDischargeSummary({...dischargeSummary, next_visit_date: e.target.value})}
                            data-testid="next-visit-input"
                          />
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="diet">Diet Instructions</Label>
                        <Input
                          id="diet"
                          value={dischargeSummary.diet_instructions}
                          onChange={(e) => setDischargeSummary({...dischargeSummary, diet_instructions: e.target.value})}
                          placeholder="e.g., Low sodium diet, diabetic diet"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="activity">Activity Restrictions</Label>
                        <Input
                          id="activity"
                          value={dischargeSummary.activity_restrictions}
                          onChange={(e) => setDischargeSummary({...dischargeSummary, activity_restrictions: e.target.value})}
                          placeholder="e.g., Bed rest for 2 weeks, no heavy lifting"
                        />
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="notes">Additional Doctor&apos;s Notes</Label>
                        <textarea
                          id="notes"
                          value={dischargeSummary.doctor_notes}
                          onChange={(e) => setDischargeSummary({...dischargeSummary, doctor_notes: e.target.value})}
                          className="w-full min-h-[60px] px-3 py-2 border border-slate-200 rounded-md bg-white"
                          placeholder="Any additional observations..."
                        />
                      </div>
                    </div>
                  </TabsContent>

                  {/* Billing Tab */}
                  <TabsContent value="billing" className="space-y-4">
                    {/* Generate Billing Option */}
                    <div className="flex items-center space-x-2 p-4 bg-purple-100 border border-blue-200 rounded-md">
                      <Checkbox
                        id="generate-bill"
                        checked={generateBill}
                        onCheckedChange={setGenerateBill}
                        data-testid="generate-bill-checkbox"
                      />
                      <label
                        htmlFor="generate-bill"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 cursor-pointer"
                      >
                        Generate billing invoice for this discharge
                      </label>
                    </div>

                    {/* Billing Items */}
                    {generateBill && (
                      <div className="space-y-4">
                        {/* Add Fixed Charge from Master */}
                        <Card className="border-purple-200 bg-purple-50">
                          <CardContent className="p-4">
                            <Label className="text-sm font-medium text-purple-800 mb-2 block flex items-center gap-2">
                              <Package className="h-4 w-4" />
                              Add Fixed Charge from Master
                            </Label>
                            <FixedChargeSearchDropdown
                              charges={fixedCharges}
                              onSelect={handleAddFixedCharge}
                              placeholder="Search fixed charges (e.g., Room Rent, Nursing Care, ICU)..."
                            />
                          </CardContent>
                        </Card>

                        {/* Billing Items List */}
                        <div className="space-y-3">
                          <div className="flex items-center justify-between">
                            <Label className="text-base font-semibold flex items-center gap-2">
                              <DollarSign className="h-4 w-4" />
                              Billing Items
                            </Label>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={handleAddBillingItem}
                              data-testid="add-billing-item-button"
                            >
                              <Plus className="h-4 w-4 mr-1" />
                              Custom Item
                            </Button>
                          </div>
                          
                          {billingItems.map((item, index) => (
                            <div key={index} className={`p-3 rounded-lg border ${item.is_fixed_charge ? 'bg-purple-50 border-purple-200' : 'bg-slate-50 border-slate-200'}`}>
                              <div className="flex items-center gap-2 mb-2">
                                {item.is_fixed_charge && (
                                  <Badge className="bg-purple-100 text-purple-700 text-[10px]">
                                    <Package className="h-3 w-3 mr-1" />
                                    Fixed Charge
                                  </Badge>
                                )}
                                {item.category && (
                                  <Badge variant="outline" className="text-[10px]">{item.category}</Badge>
                                )}
                                <div className="ml-auto">
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemoveBillingItem(index)}
                                    className="h-8 w-8 p-0 text-slate-400 hover:text-red-500"
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              </div>
                              <div className="grid grid-cols-12 gap-3">
                                <div className="col-span-5">
                                  <Label className="text-xs text-slate-500">Description</Label>
                                  <Input
                                    placeholder="Item description"
                                    value={item.description}
                                    onChange={(e) => handleBillingItemChange(index, 'description', e.target.value)}
                                    data-testid={`billing-item-description-${index}`}
                                    readOnly={item.is_fixed_charge}
                                    className={item.is_fixed_charge ? 'bg-white/50' : ''}
                                  />
                                </div>
                                <div className="col-span-2">
                                  <Label className="text-xs text-slate-500">Rate (₹)</Label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    placeholder="0.00"
                                    value={item.amount}
                                    onChange={(e) => handleBillingItemChange(index, 'amount', e.target.value)}
                                    data-testid={`billing-item-amount-${index}`}
                                    readOnly={item.is_fixed_charge}
                                    className={item.is_fixed_charge ? 'bg-white/50 font-mono' : 'font-mono'}
                                  />
                                </div>
                                <div className="col-span-2">
                                  <Label className="text-xs text-slate-500">Qty</Label>
                                  <Input
                                    type="number"
                                    min="1"
                                    placeholder="1"
                                    value={item.quantity}
                                    onChange={(e) => handleBillingItemChange(index, 'quantity', e.target.value)}
                                    data-testid={`billing-item-qty-${index}`}
                                  />
                                </div>
                                <div className="col-span-1">
                                  <Label className="text-xs text-slate-500">Tax%</Label>
                                  <Input
                                    type="number"
                                    step="0.01"
                                    min="0"
                                    max="100"
                                    placeholder="0"
                                    value={item.tax_percentage}
                                    onChange={(e) => handleBillingItemChange(index, 'tax_percentage', e.target.value)}
                                    readOnly={item.is_fixed_charge}
                                    className={item.is_fixed_charge ? 'bg-white/50' : ''}
                                  />
                                </div>
                                <div className="col-span-2">
                                  <Label className="text-xs text-slate-500">Total</Label>
                                  <div className="h-10 px-3 py-2 bg-slate-100 rounded-md font-mono text-sm flex items-center justify-end">
                                    ₹{((item.amount || 0) * (item.quantity || 1) * (1 + (item.tax_percentage || 0) / 100)).toFixed(2)}
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}

                          {/* Billing Summary */}
                          <Card className="border-slate-200">
                            <CardContent className="p-4">
                              <div className="space-y-2 text-sm">
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Subtotal:</span>
                                  <span className="font-mono">₹{billingTotals.subtotal}</span>
                                </div>
                                <div className="flex justify-between">
                                  <span className="text-slate-600">Tax:</span>
                                  <span className="font-mono">₹{billingTotals.tax}</span>
                                </div>
                                <div className="flex justify-between border-t pt-2 font-bold">
                                  <span>Grand Total:</span>
                                  <span className="font-mono text-lg text-purple-700" data-testid="billing-total">₹{billingTotals.total}</span>
                                </div>
                              </div>
                            </CardContent>
                          </Card>

                          {/* Warning if no amount */}
                          {calculateTotal() === 0 && (
                            <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-md">
                              <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 flex-shrink-0" />
                              <p className="text-sm text-amber-900">
                                Please add billing amounts or uncheck the billing option to proceed with discharge only.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {!generateBill && (
                      <div className="p-4 bg-slate-50 border border-slate-200 rounded-md text-center">
                        <p className="text-sm text-slate-600">Billing will not be generated for this discharge</p>
                      </div>
                    )}
                  </TabsContent>
                </Tabs>

                {/* Action Buttons */}
                <div className="flex gap-3 pt-4 border-t">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setDischargeDialogOpen(false);
                      setSelectedAdmission(null);
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleDischargeWithBilling}
                    data-testid="confirm-discharge-button"
                    className="flex-1 bg-[#0f766e] hover:bg-[#115e59] text-white"
                  >
                    <BedDouble className="mr-2 h-4 w-4" />
                    Complete Discharge
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </motion.div>
    </div>
  );
}